import { useId, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Badge, Button, cn, Field, Icon, NotProvided } from '@evogard/ui';
import { loadFailureMessage } from '@evogard/domain';
import { api } from '../../../lib/api.js';
import { useDebouncedTerm } from '../../../lib/debounce.js';
import { useSessionContext } from '../../../lib/session.js';
import {
  VEHICLE_SITUATION_FRAME,
  catalogVehicleSituation,
} from '../../../components/vehicle-situation.js';
import { useClaimPermissions, useErpVehicle, type CatalogVehicle } from './useClaims.js';

/**
 * O veículo do associado — procurado no espelho do SGA, guardado pelo código.
 *
 * O sinistro do associado guarda só `vehicle_code` (sem estrangeira, convenção
 * 10): placa, modelo e dono são lidos do espelho, pela rota do `erp` (regra 1),
 * e nunca copiados. Este campo procura pela placa, pelo documento ou pelo nome
 * do dono — o que quem atende tem na mão — e devolve o código.
 *
 * **Sem `erp.catalog:ver` não há busca**, e o campo vira o código digitado à
 * mão, dizendo por quê. O sinistro continua podendo ser aberto: o que falta é
 * a conferência, não o dado.
 *
 * **A situação do carro aparece como na Assistência 24h** (03/11/2026): o selo
 * em cada linha da busca e a moldura do carro escolhido na cor que a casa julga
 * (`vehicleSituationTone`). A cor avisa e não bloqueia — quem decide abrir o
 * aviso de um carro cancelado é quem atende (regra 14).
 *
 * **`useEligibility` decide quem pode ser escolhido** — Periféricos só abre
 * para carro com a cobertura no contrato, e cada linha da busca diz a dele.
 * É um hook, chamado com os códigos da página: precisa ser uma função de
 * módulo, estável entre renderizações. Sem ele, todo veículo é escolhível.
 */
export function ErpVehicleField({
  value,
  onChange,
  useEligibility = useAnyVehicle,
}: {
  value: string;
  onChange: (code: string) => void;
  useEligibility?: VehicleEligibility;
}) {
  const { canSearchVehicles } = useClaimPermissions();
  const legendId = useId();

  return (
    <fieldset
      aria-labelledby={legendId}
      className="flex flex-col gap-3 rounded-borda border border-borda-suave p-4"
    >
      <legend id={legendId} className="px-1 text-xs font-medium text-texto">
        Veículo do associado
        <span aria-hidden="true" className="ml-0.5 text-erro">
          *
        </span>
        <span className="sr-only"> (obrigatório)</span>
      </legend>

      {!canSearchVehicles ? (
        <Field
          label="Código do veículo no SGA"
          required
          autoComplete="off"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Como aparece no SGA"
          help="Sem acesso ao cadastro do ERP nesta conta, a busca pela placa não está disponível — digite o código."
        />
      ) : value ? (
        <Chosen
          code={value}
          onClear={() => onChange('')}
          useEligibility={useEligibility}
        />
      ) : (
        /*
         * `erpCode`, e não `code`: o sinistro guarda `claims.vehicle_code`, que
         * é o código do SGA — o mesmo que o campo manual acima pede. A lista do
         * catálogo passou a devolver os dois em 05/10/2026, e mandar o da casa
         * (`VE-000123`) gravaria no sinistro uma chave que a busca do catálogo
         * não casa: a ficha diria "o espelho não tem este código" sobre um
         * veículo escolhido na própria tela.
         *
         * O `?? ''` nunca acontece: `Search` já peneira quem não tem `erpCode`.
         */
        <Search
          onPick={(v) => onChange(v.erpCode ?? '')}
          useEligibility={useEligibility}
        />
      )}
    </fieldset>
  );
}

/** O que a linha de um veículo diz sobre poder escolhê-lo. */
export interface VehicleVerdict {
  pickable: boolean;
  /** O selo da linha — a cobertura, ou por que não dá. */
  note: ReactNode;
}

/**
 * Quem pode ser escolhido, pelos códigos do SGA de uma página da busca.
 * `of` devolve `undefined` enquanto a resposta não chegou — e a linha sem
 * veredito não se escolhe, para ninguém abrir em cima do que ainda não se sabe.
 */
export type VehicleEligibility = (codes: readonly string[]) => {
  error: string | null;
  of: (code: string) => VehicleVerdict | undefined;
};

const ANY: VehicleVerdict = { pickable: true, note: null };

/** O padrão: todo veículo com código no SGA se escolhe. */
function useAnyVehicle(): ReturnType<VehicleEligibility> {
  return { error: null, of: () => ANY };
}

function Chosen({
  code,
  onClear,
  useEligibility,
}: {
  code: string;
  onClear: () => void;
  useEligibility: VehicleEligibility;
}) {
  const query = useErpVehicle(code);
  const v = query.data;
  const eligibility = useEligibility([code]);
  const verdict = eligibility.of(code);

  const tone = v ? catalogVehicleSituation(v.situacao).tone : 'neutro';
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-3 rounded-xl border p-3',
        VEHICLE_SITUATION_FRAME[tone],
      )}
    >
      <div className="flex min-w-0 flex-col">
        {query.isPending ? (
          <span
            className="h-10 w-48 animate-pulse rounded-borda bg-superficie-suave"
            aria-busy="true"
          />
        ) : v ? (
          <VehicleLines vehicle={v} />
        ) : (
          <>
            <span className="text-sm font-medium text-texto">Código {code}</span>
            <span className="text-xs text-texto-suave">
              {query.isError
                ? query.error.message
                : 'O espelho do SGA não tem este código — a placa e o dono não podem ser conferidos agora.'}
            </span>
          </>
        )}
        {eligibility.error ? (
          <span className="mt-1.5 text-xs text-erro">{eligibility.error}</span>
        ) : (
          verdict?.note && <span className="mt-1.5">{verdict.note}</span>
        )}
      </div>
      <Button type="button" variant="discreto" size="sm" onClick={onClear}>
        Trocar veículo
      </Button>
    </div>
  );
}

function VehicleLines({ vehicle: v }: { vehicle: CatalogVehicle }) {
  const situation = catalogVehicleSituation(v.situacao);
  return (
    <>
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-sm font-medium text-texto tabular-nums">
          {v.placa ?? <NotProvided>sem placa</NotProvided>}
          {/* o código do SGA, que é o que o sinistro guarda e o que se dita */}
          <span className="font-normal text-texto-suave">
            {' '}
            · código {v.erpCode ?? '—'}
          </span>
        </span>
        <Badge tone={situation.tone}>{situation.label}</Badge>
      </span>
      <span className="text-xs text-texto-suave">
        {[v.marca, v.modelo].filter(Boolean).join(' ') || 'modelo não informado'}
        {v.anoModelo ? ` · ${v.anoModelo}` : ''}
        {' · '}
        {v.owner?.nome ?? 'sem associado no espelho'}
      </span>
    </>
  );
}

function Search({
  onPick,
  useEligibility,
}: {
  onPick: (vehicle: CatalogVehicle) => void;
  useEligibility: VehicleEligibility;
}) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const [search, setSearch] = useState('');
  const term = useDebouncedTerm(search).trim();
  const searching = term.length >= 2;

  const query = useQuery({
    queryKey: ['sinistros', 'busca-veiculo-sga', tenantId, term],
    queryFn: async () => {
      const r = await api.erp.searchVehicles({ body: { term, page: 1, perPage: 8 } });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'os veículos do SGA'));
      return r.body;
    },
    enabled: searching && tenantId !== undefined,
  });

  const result = query.data;
  /*
   * Fora da lista o veículo SEM código no ERP. O sinistro guarda
   * `claims.vehicle_code`, que é a chave do SGA: um veículo nascido na casa não
   * tem o que gravar ali, e oferecê-lo deixaria o clique limpar o campo em
   * silêncio. Hoje a lista inteira vem do espelho e nenhum cai nesta peneira;
   * a guarda existe para o dia em que cair.
   */
  const items =
    searching && result?.estado === 'lida'
      ? result.items.filter((v) => v.erpCode !== null)
      : [];
  const eligibility = useEligibility(items.map((v) => v.erpCode!));

  return (
    <div className="flex flex-col gap-3">
      <Field
        label="Buscar veículo no SGA"
        icon="busca"
        autoComplete="off"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        /* busca não envia nada: este campo mora dentro do formulário do sinistro,
           e o Enter aqui enviaria o sinistro inteiro pela metade */
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault();
        }}
        placeholder="Placa, código, CPF/CNPJ ou nome do dono"
      />

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      {eligibility.error && <Alert tone="erro">{eligibility.error}</Alert>}

      {searching && query.isPending && (
        <div aria-busy="true" className="flex flex-col gap-2">
          <div className="h-14 animate-pulse rounded-borda bg-superficie-suave" />
          <div className="h-14 animate-pulse rounded-borda bg-superficie-suave" />
        </div>
      )}

      {items.length > 0 && (
        <ul className="flex flex-col gap-2" aria-label="Veículos encontrados no SGA">
          {items.map((v) => {
            const verdict = eligibility.of(v.erpCode!);
            const pickable = verdict?.pickable === true;
            return (
              <li key={v.erpCode ?? v.code}>
                <button
                  type="button"
                  disabled={!pickable}
                  aria-busy={verdict === undefined || undefined}
                  onClick={() => onPick(v)}
                  className={cn(
                    'flex min-h-11 w-full items-center justify-between gap-3 rounded-borda border border-borda-forte px-3 py-2 text-left',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primaria-texto',
                    pickable
                      ? 'transition-[border-color,background-color] hover:border-texto-suave hover:bg-superficie-suave'
                      : 'cursor-not-allowed border-borda-suave bg-superficie-suave',
                  )}
                >
                  <span className="flex min-w-0 flex-col">
                    <VehicleLines vehicle={v} />
                    {verdict?.note && <span className="mt-1.5">{verdict.note}</span>}
                  </span>
                  {pickable && (
                    <Icon
                      nome="seta-direita"
                      size={16}
                      className="shrink-0 text-texto-suave"
                    />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {searching && result?.estado === 'lida' && items.length === 0 && (
        <p className="text-sm text-texto-suave">
          Nenhum veículo com &ldquo;{term}&rdquo; no espelho desta base.
        </p>
      )}
    </div>
  );
}
